/**
 * Iter-184 — Remedial Action Schedule (shared).
 *
 * The building report PDF has always derived its "Remedial Action Schedule"
 * from the latest inspection per door. That derivation now lives here so the
 * Excel / CSV export (Building Details + report generation screen) produces
 * exactly the same lines as the PDF — same defects, risk grades, recommended
 * actions and timescales.
 */
import {
  normaliseComponents,
  defectCategoryFor,
  riskFor,
  recommendedActionFor,
  remediationTimescaleFor,
  doorsetNeedsReplacement,
  DEFECT_CATEGORY_ORDER,
  type RiskLevel,
} from './reportChecklist';
import { isDoubleDoor, leafSuffix } from './doorLeaves';

export interface RemedialAction {
  doorRef: string;
  location: string;
  defect: string;
  risk: RiskLevel;
  action: string;
  timescale: string;
  doorId?: string;
  inspectionDate?: string | null;
  /** Iter-185 — stable key for in-app tracking: component key or `doorset_replacement`. */
  defectKey: string;
}

// Iter-185 — Remedial Tracking In-App. One row per (door, defect) the
// manager has touched; see backend routers/remedial_tracking.py.
export type RemedialTrackingStatus = 'open' | 'assigned' | 'in_progress' | 'completed';
export interface RemedialTrackingRow {
  id?: string;
  door_id: string;
  defect_key: string;
  status: RemedialTrackingStatus;
  status_label?: string;
  assigned_to: string;
  date_completed: string;
  notes: string;
  updated_by_name?: string | null;
  updated_at?: string | null;
}
export const TRACKING_STATUS_LABEL: Record<RemedialTrackingStatus, string> = {
  open: 'Open', assigned: 'Assigned', in_progress: 'In progress', completed: 'Completed',
};
export const TRACKING_STATUS_ORDER: RemedialTrackingStatus[] = ['open', 'assigned', 'in_progress', 'completed'];
export const trackingKey = (doorId: string, defectKey: string) => `${doorId}::${defectKey}`;
export const indexTracking = (rows: RemedialTrackingRow[] | null | undefined): Record<string, RemedialTrackingRow> => {
  const out: Record<string, RemedialTrackingRow> = {};
  for (const r of rows || []) if (r?.door_id && r?.defect_key) out[trackingKey(r.door_id, r.defect_key)] = r;
  return out;
};
export const trackingFor = (index: Record<string, RemedialTrackingRow>, a: RemedialAction): RemedialTrackingRow | undefined =>
  a.doorId ? index[trackingKey(a.doorId, a.defectKey)] : undefined;

export const RISK_ORDER: Record<RiskLevel, number> = { Critical: 0, High: 1, Medium: 2, Low: 3 };

/** Newest inspection for each door (by inspection_date). */
export const latestInspectionPerDoor = (inspections: any[]): Map<string, any> => {
  const latestByDoor = new Map<string, any>();
  for (const insp of inspections || []) {
    const id = insp?.door_id;
    if (!id) continue;
    const prev = latestByDoor.get(id);
    const prevTs = prev ? new Date(prev.inspection_date || 0).getTime() : -Infinity;
    const nextTs = new Date(insp.inspection_date || 0).getTime();
    if (!prev || nextTs > prevTs) latestByDoor.set(id, insp);
  }
  return latestByDoor;
};

export const DOORSET_REPLACEMENT_ACTION =
  'Replace door, frame and all associated ironmongery/furniture with a third-party certificated fire doorset of the appropriate fire-resistance rating (e.g. FD30/FD60), installed to BS 8214 and the manufacturer\u2019s instructions. Implement interim mitigation (e.g. temporary signage, fire warden checks, keeping compartment clear) while awaiting the manufactured doorset.';

/**
 * Build the schedule + the 9 executive-summary defect buckets from the
 * latest inspection per door. Sorted Critical → Low, then by door.
 */
export const buildRemedialSchedule = (doors: any[], latestInspections: any[]) => {
  const defectCounts: Record<string, number> = {};
  for (const cat of DEFECT_CATEGORY_ORDER) defectCounts[cat] = 0;

  const remedialActions: RemedialAction[] = [];
  const doorById = new Map<string, any>();
  for (const d of doors || []) doorById.set(d.id, d);

  for (const insp of latestInspections) {
    const comps = normaliseComponents(insp);
    const failedComps = comps.filter((c) => c.status === 'fail');
    const door = doorById.get(insp.door_id) || {};
    const doorRefBase = door.door_reference || (door.apartment_number ? `Apt ${door.apartment_number}` : '(unknown door)');
    const doorRef = door.apartment_number && door.door_reference
      ? `${door.door_reference} (Apt ${door.apartment_number})`
      : doorRefBase;
    const location = door.location || '';
    const common = { doorRef, location, doorId: insp.door_id, inspectionDate: insp.inspection_date || null };
    // Iter-188 — double doors: each failed component carries the leaf it
    // is on; the schedule line is labelled "<defect> — Leaf 2" etc.
    const dbl = isDoubleDoor(door, insp);
    const leafByKey = new Map<string, string>();
    for (const fc of (insp.failed_component_photos || [])) if (fc?.key && fc?.leaf) leafByKey.set(fc.key, fc.leaf);

    // Full doorset replacement supersedes every per-component line.
    if (doorsetNeedsReplacement(insp)) {
      remedialActions.push({
        ...common,
        defectKey: 'doorset_replacement',
        defect: 'Full doorset replacement required',
        risk: 'Critical',
        action: DOORSET_REPLACEMENT_ACTION,
        timescale: remediationTimescaleFor({ risk: 'Critical', isFullDoorsetReplacement: true }),
      });
      defectCounts['Non-compliant Doorset'] = (defectCounts['Non-compliant Doorset'] || 0) + 1;
      continue;
    }

    for (const c of failedComps) {
      const cat = defectCategoryFor(c.key);
      defectCounts[cat] = (defectCounts[cat] || 0) + 1;
      const risk = riskFor(c.key);
      remedialActions.push({
        ...common,
        defectKey: c.key,
        defect: `${c.label}${leafSuffix(leafByKey.get(c.key), dbl)}`,
        risk,
        action: recommendedActionFor(c.key),
        timescale: remediationTimescaleFor({ key: c.key, risk }),
      });
    }

    // Failure-reason flow entries not already covered by the component pass.
    const fcps: any[] = (insp.failed_component_photos || []).filter((f: any) => f?.key);
    for (const fc of fcps) {
      const defectLabel = `${fc.question || fc.key}${leafSuffix(fc.leaf, dbl)}`;
      const alreadyOnSchedule = remedialActions.some(
        (r) => r.doorRef === doorRef && (r.defectKey === fc.key || r.defect === defectLabel),
      );
      if (alreadyOnSchedule) continue;
      const risk: RiskLevel = (fc.risk as RiskLevel) || riskFor(fc.key);
      remedialActions.push({
        ...common,
        defectKey: fc.key,
        defect: defectLabel,
        risk,
        action: recommendedActionFor(fc.key),
        timescale: remediationTimescaleFor({ key: fc.key, risk }),
      });
    }
  }

  remedialActions.sort((a, b) => {
    if (RISK_ORDER[a.risk] !== RISK_ORDER[b.risk]) return RISK_ORDER[a.risk] - RISK_ORDER[b.risk];
    return a.doorRef.localeCompare(b.doorRef);
  });

  return { remedialActions, defectCounts };
};

// ─── Iter-189 — Consolidated remedial recommendations ────────────────
//
// The per-door schedule repeats the same recommended action on every door
// row. The report now ALSO states each fix once — "R1 · Replace intumescent
// strips — 14 doors: FD-01, FD-03 …" — grouped by (defect, action), ranked
// by the highest risk in the group, then by how many doors it touches.

export interface ConsolidatedFixDoor {
  doorRef: string;
  location: string;
  doorId?: string;
  leaf?: string;          // "Leaf 2" etc. when the defect was leaf-specific
  completed: boolean;     // tracked as completed in-app
}

export interface ConsolidatedFix {
  ref: string;            // "R1"
  defect: string;         // leaf suffix removed
  action: string;
  risk: RiskLevel;        // highest across the group
  timescale: string;      // for that highest risk
  doors: ConsolidatedFixDoor[];
  completed: number;
}

const LEAF_TAIL = /\s+—\s+(Leaf 1|Leaf 2|Both leaves)$/;

export const consolidateRemedialActions = (
  rows: RemedialAction[],
  trackingIdx?: Record<string, RemedialTrackingRow>,
): ConsolidatedFix[] => {
  const groups = new Map<string, ConsolidatedFix>();
  for (const r of rows) {
    const leafMatch = r.defect.match(LEAF_TAIL);
    const defect = r.defect.replace(LEAF_TAIL, '').trim();
    const key = `${defect.toLowerCase()}|${r.action.trim().toLowerCase()}`;
    const t = trackingIdx ? trackingFor(trackingIdx, r) : undefined;
    const done = t?.status === 'completed';
    const g = groups.get(key) || { ref: '', defect, action: r.action, risk: r.risk, timescale: r.timescale, doors: [], completed: 0 };
    if (RISK_ORDER[r.risk] < RISK_ORDER[g.risk]) { g.risk = r.risk; g.timescale = r.timescale; }
    if (!g.doors.some((d) => d.doorRef === r.doorRef && d.leaf === leafMatch?.[1])) {
      g.doors.push({ doorRef: r.doorRef, location: r.location, doorId: r.doorId, leaf: leafMatch?.[1], completed: done });
      if (done) g.completed += 1;
    }
    groups.set(key, g);
  }
  const fixes = [...groups.values()].sort((a, b) => {
    if (RISK_ORDER[a.risk] !== RISK_ORDER[b.risk]) return RISK_ORDER[a.risk] - RISK_ORDER[b.risk];
    if (a.doors.length !== b.doors.length) return b.doors.length - a.doors.length;
    return a.defect.localeCompare(b.defect);
  });
  fixes.forEach((f, i) => {
    f.ref = `R${i + 1}`;
    f.doors.sort((x, y) => x.doorRef.localeCompare(y.doorRef, undefined, { numeric: true }));
  });
  return fixes;
};

/** Rows in the shape the export endpoint expects (tracking values included). */
export const remedialScheduleExportRows = (doors: any[], inspections: any[], tracking?: RemedialTrackingRow[] | null) => {
  const latest = Array.from(latestInspectionPerDoor(inspections).values());
  const { remedialActions } = buildRemedialSchedule(doors, latest);
  const idx = indexTracking(tracking);
  return remedialActions.map((r) => {
    const t = trackingFor(idx, r);
    return {
      door: r.doorRef,
      location: r.location,
      defect: r.defect,
      risk: r.risk,
      action: r.action,
      timescale: r.timescale,
      inspected: r.inspectionDate ? String(r.inspectionDate).slice(0, 10) : '',
      status: t ? TRACKING_STATUS_LABEL[t.status] || 'Open' : 'Open',
      assigned_to: t?.assigned_to || '',
      date_completed: t?.date_completed || '',
      notes: t?.notes || '',
    };
  });
};
